import React, { useState, useEffect, useCallback } from 'react';
import {
  RefreshCw, CheckSquare, TrendingUp, DollarSign, Inbox,
  Calendar, History, CheckCircle, XCircle, RotateCcw, Clock, User, ExternalLink,
  LayoutGrid, List, FileText,
} from 'lucide-react';
import { getPendientes, getHistorial } from '../../api/aprobaciones';
import { revertirIngreso, aprobarIngreso, rechazarIngreso } from '../../api/payrollIncomes';
import { personalActionsAPI } from '../../api/personal-actions';
import { absencesAPI } from '../../api/absences';
import { solicitudesAPI } from '../../api/solicitudes';
import AprobacionCard from './AprobacionCard';

const TIPO_LABELS = {
  accion_personal: 'Acción de Personal',
  ingreso_nomina:  'Ingreso Variable',
  ausencia:        'Ausencia',
  solicitud:       'Solicitud',
};

const ESTADO_BADGE = {
  aprobado:   'bg-green-100 text-green-700',
  aprobada:   'bg-green-100 text-green-700',
  rechazado:  'bg-red-100 text-red-700',
  rechazada:  'bg-red-100 text-red-700',
  revocado:   'bg-gray-100 text-gray-600',
  cancelado:  'bg-gray-100 text-gray-600',
  cancelada:  'bg-gray-100 text-gray-600',
  completado: 'bg-blue-100 text-blue-700',
  completada: 'bg-blue-100 text-blue-700',
};

function fmt(n) {
  return parseFloat(n || 0).toLocaleString('es-DO', { minimumFractionDigits: 2 });
}
function fmtDate(d) {
  if (!d) return '—';
  const s = String(d).split('T')[0];
  return new Date(s + 'T12:00:00').toLocaleDateString('es-DO', { day: '2-digit', month: 'short', year: 'numeric' });
}

const TABS_PENDIENTES = [
  { id: 'todas',           label: 'Todas',              icon: CheckSquare, tipo: null              },
  { id: 'solicitud',       label: 'Solicitudes',        icon: FileText,    tipo: 'solicitud'       },
  { id: 'accion_personal', label: 'Acciones',           icon: TrendingUp,  tipo: 'accion_personal' },
  { id: 'ingreso_nomina',  label: 'Ingresos',           icon: DollarSign,  tipo: 'ingreso_nomina'  },
  { id: 'ausencia',        label: 'Ausencias',          icon: Calendar,    tipo: 'ausencia'        },
];

const TIPO_MODULO = {
  accion_personal: 'personnel-actions',
  ingreso_nomina:  'variable-incomes',
  ausencia:        'absences',
  solicitud:       'solicitudes',
};

export default function AprobacionesModule({ onNavigateTo }) {
  const [vista,         setVista]         = useState('pendientes'); // 'pendientes' | 'historial'
  const [tabActivo,     setTabActivo]     = useState('todas');
  const [pendientes,    setPendientes]    = useState([]);
  const [historial,     setHistorial]     = useState([]);
  const [cargando,      setCargando]      = useState(true);
  const [cargandoHist,  setCargandoHist]  = useState(false);
  const [error,         setError]         = useState(null);

  const [vistaCards,     setVistaCards]     = useState(true);

  // ── Revertir ingreso aprobado ──────────────────────────────
  const [revirtiendoId, setRevirtiendoId]   = useState(null);
  const [motivoRevert,  setMotivoRevert]    = useState('');
  const [showRevertModal, setShowRevertModal] = useState(false);
  const [itemRevertir, setItemRevertir]     = useState(null);

  // ── Rechazar desde vista tabla ─────────────────────────────
  const [rejectModal,  setRejectModal]  = useState({ show: false, item: null, motivo: '' });
  const [procesandoId, setProcesandoId] = useState(null);

  const cargarPendientes = useCallback(async () => {
    setCargando(true);
    setError(null);
    try {
      const data = await getPendientes();
      setPendientes(data.pendientes || []);
    } catch (err) {
      setError('Error al cargar aprobaciones pendientes');
    } finally {
      setCargando(false);
    }
  }, []);

  const cargarHistorial = useCallback(async () => {
    setCargandoHist(true);
    try {
      const data = await getHistorial({ limit: 150 });
      setHistorial(data.historial || []);
    } catch (err) {
      setError('Error al cargar historial');
    } finally {
      setCargandoHist(false);
    }
  }, []);

  useEffect(() => { cargarPendientes(); }, [cargarPendientes]);

  useEffect(() => {
    if (vista === 'historial' && historial.length === 0) cargarHistorial();
  }, [vista]);

  const contarPorTipo = (tipo) =>
    tipo ? pendientes.filter(p => p.tipo === tipo).length : pendientes.length;

  const pendientesFiltrados = tabActivo === 'todas'
    ? pendientes
    : pendientes.filter(p => p.tipo === tabActivo);

  const handleVerDetalle = (item) => {
    if (!onNavigateTo) return;
    const module = TIPO_MODULO[item.tipo];
    if (!module) return;

    if (item.tipo === 'ingreso_nomina') {
      onNavigateTo(module, {
        id: item.id,
        employeeId: item.employee_id,
        periodo: item.periodo,
        quincena: item.quincena
      });
    } else {
      onNavigateTo(module, { id: item.id });
    }
  };

  const handleAprobarTabla = async (item) => {
    if (!window.confirm('¿Confirmas la aprobación?')) return;
    setProcesandoId(item.id);
    try {
      if (item.tipo === 'ingreso_nomina')       await aprobarIngreso(item.id);
      else if (item.tipo === 'accion_personal') await personalActionsAPI.approve(item.id);
      else if (item.tipo === 'ausencia')        await absencesAPI.aprobar(item.id);
      else if (item.tipo === 'solicitud')       await solicitudesAPI.aprobar(item.id);
      cargarPendientes();
    } catch (err) {
      alert('Error al aprobar: ' + (err.response?.data?.error || err.message));
    } finally {
      setProcesandoId(null);
    }
  };

  const handleRechazarTabla = async () => {
    if (!rejectModal.motivo.trim()) return;
    setProcesandoId(rejectModal.item.id);
    try {
      const { item, motivo } = rejectModal;
      if (item.tipo === 'ingreso_nomina')       await rechazarIngreso(item.id, motivo);
      else if (item.tipo === 'accion_personal') await personalActionsAPI.reject(item.id, motivo);
      else if (item.tipo === 'ausencia')        await absencesAPI.rechazar(item.id, motivo);
      else if (item.tipo === 'solicitud')       await solicitudesAPI.rechazar(item.id, { motivo_rechazo: motivo });
      setRejectModal({ show: false, item: null, motivo: '' });
      cargarPendientes();
    } catch (err) {
      alert('Error al rechazar: ' + (err.response?.data?.error || err.message));
    } finally {
      setProcesandoId(null);
    }
  };

  const handleRevertir = async () => {
    if (!motivoRevert.trim()) return;
    setRevirtiendoId(itemRevertir.id);
    try {
      await revertirIngreso(itemRevertir.id, motivoRevert);
      setShowRevertModal(false);
      setMotivoRevert('');
      setItemRevertir(null);
      cargarHistorial();
    } catch (err) {
      alert('Error al revertir: ' + (err.response?.data?.error || err.message));
    } finally {
      setRevirtiendoId(null);
    }
  };

  return (
    <div className="space-y-6">

      {/* Vista selector: Pendientes / Historial */}
      <div className="flex items-center justify-between gap-4 flex-wrap">
        <div className="flex gap-1 bg-gray-100 p-1 rounded-xl w-fit">
          <button
            onClick={() => setVista('pendientes')}
            className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-all ${
              vista === 'pendientes' ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500 hover:text-gray-700'
            }`}
          >
            <CheckSquare size={14} />
            Pendientes
            {pendientes.length > 0 && (
              <span className="ml-1 text-xs font-bold px-1.5 py-0.5 rounded-full bg-orange-100 text-orange-700">
                {pendientes.length}
              </span>
            )}
          </button>
          <button
            onClick={() => setVista('historial')}
            className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-all ${
              vista === 'historial' ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500 hover:text-gray-700'
            }`}
          >
            <History size={14} />
            Historial
          </button>
        </div>

        <button
          onClick={vista === 'pendientes' ? cargarPendientes : cargarHistorial}
          disabled={cargando || cargandoHist}
          className="flex items-center gap-2 px-4 py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-lg text-sm font-medium transition-colors disabled:opacity-50"
        >
          <RefreshCw className={`w-4 h-4 ${(cargando || cargandoHist) ? 'animate-spin' : ''}`} />
          Actualizar
        </button>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 rounded-xl p-4 text-red-700 text-sm">{error}</div>
      )}

      {/* ── VISTA PENDIENTES ──────────────────────────────────── */}
      {vista === 'pendientes' && (
        <>
          {/* Filtros + toggle de vista */}
          <div className="flex items-center justify-between gap-3 flex-wrap">
            <div className="flex gap-1 bg-gray-100 p-1 rounded-xl overflow-x-auto">
              {TABS_PENDIENTES.map(tab => {
                const Icon = tab.icon;
                const count = contarPorTipo(tab.tipo);
                const isActive = tabActivo === tab.id;
                return (
                  <button
                    key={tab.id}
                    onClick={() => setTabActivo(tab.id)}
                    className={`flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium whitespace-nowrap transition-all flex-shrink-0 ${
                      isActive ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-600 hover:text-gray-900 hover:bg-white/50'
                    }`}
                  >
                    <Icon className="w-4 h-4" />
                    <span>{tab.label}</span>
                    <span className={`ml-1 text-xs font-bold px-1.5 py-0.5 rounded-full ${
                      isActive
                        ? count > 0 ? 'bg-purple-100 text-purple-700' : 'bg-gray-100 text-gray-500'
                        : count > 0 ? 'bg-orange-100 text-orange-700' : 'bg-gray-200 text-gray-400'
                    }`}>{count}</span>
                  </button>
                );
              })}
            </div>
            <div className="flex gap-1 bg-gray-100 p-1 rounded-lg">
              <button
                onClick={() => setVistaCards(true)}
                title="Vista tarjetas"
                className={`p-1.5 rounded transition-all ${vistaCards ? 'bg-white shadow-sm text-gray-900' : 'text-gray-400 hover:text-gray-600'}`}
              >
                <LayoutGrid size={15} />
              </button>
              <button
                onClick={() => setVistaCards(false)}
                title="Vista tabla"
                className={`p-1.5 rounded transition-all ${!vistaCards ? 'bg-white shadow-sm text-gray-900' : 'text-gray-400 hover:text-gray-600'}`}
              >
                <List size={15} />
              </button>
            </div>
          </div>

          {cargando ? (
            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
              {[1,2,3].map(i => (
                <div key={i} className="bg-white rounded-xl border-2 border-gray-100 p-4 animate-pulse">
                  <div className="flex gap-3">
                    <div className="w-10 h-10 bg-gray-200 rounded-full" />
                    <div className="flex-1 space-y-2">
                      <div className="h-4 bg-gray-200 rounded w-3/4" />
                      <div className="h-3 bg-gray-200 rounded w-1/2" />
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : pendientesFiltrados.length === 0 ? (
            <div className="text-center py-16">
              <div className="w-16 h-16 bg-green-50 rounded-full flex items-center justify-center mx-auto mb-4">
                <Inbox className="w-8 h-8 text-green-400" />
              </div>
              <h4 className="text-lg font-semibold text-gray-700 mb-1">¡Todo al día!</h4>
              <p className="text-sm text-gray-400">No hay items pendientes de aprobación.</p>
            </div>
          ) : vistaCards ? (
            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
              {pendientesFiltrados.map(item => (
                <AprobacionCard
                  key={`${item.tipo}-${item.id}`}
                  item={item}
                  onActualizar={cargarPendientes}
                />
              ))}
            </div>
          ) : (
            /* ── Vista tabla pendientes ── */
            <div className="bg-white border border-gray-200 rounded-xl overflow-hidden">
              <table className="w-full text-sm">
                <thead className="bg-gray-50 border-b border-gray-200">
                  <tr>
                    <th className="text-left px-4 py-3 font-semibold text-gray-600 text-xs">Empleado</th>
                    <th className="text-left px-4 py-3 font-semibold text-gray-600 text-xs">Tipo</th>
                    <th className="text-left px-4 py-3 font-semibold text-gray-600 text-xs">Descripción</th>
                    <th className="text-left px-4 py-3 font-semibold text-gray-600 text-xs">Solicitado por</th>
                    <th className="text-left px-4 py-3 font-semibold text-gray-600 text-xs">Fecha</th>
                    <th className="px-4 py-3 text-right font-semibold text-gray-600 text-xs">Acciones</th>
                  </tr>
                </thead>
                <tbody>
                  {pendientesFiltrados.map((item, idx) => {
                    const BADGE = {
                      accion_personal: 'bg-blue-100 text-blue-700',
                      ingreso_nomina:  'bg-green-100 text-green-700',
                      ausencia:        'bg-orange-100 text-orange-700',
                      solicitud:       'bg-purple-100 text-purple-700',
                    };
                    const isProcesando = procesandoId === item.id;
                    return (
                      <tr key={`${item.tipo}-${item.id}`} className={`border-t border-gray-100 ${idx % 2 === 1 ? 'bg-gray-50/50' : 'bg-white'}`}>
                        {/* Empleado */}
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-2.5">
                            {item.empleado_foto ? (
                              <img src={item.empleado_foto} alt={item.empleado_nombre} className="w-8 h-8 rounded-full object-cover border border-gray-200 flex-shrink-0" />
                            ) : (
                              <div className="w-8 h-8 rounded-full bg-gray-200 flex items-center justify-center text-xs font-bold text-gray-500 flex-shrink-0">
                                {item.empleado_nombre?.[0]?.toUpperCase() || '?'}
                              </div>
                            )}
                            <div>
                              <p className="font-medium text-gray-900 whitespace-nowrap">{item.empleado_nombre}</p>
                              {item.empleado_codigo && <p className="text-xs text-gray-400">#{item.empleado_codigo}</p>}
                            </div>
                          </div>
                        </td>
                        {/* Tipo */}
                        <td className="px-4 py-3 whitespace-nowrap">
                          <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold ${BADGE[item.tipo] || 'bg-gray-100 text-gray-600'}`}>
                            {TIPO_LABELS[item.tipo] || item.tipo}
                          </span>
                          {item.descripcion_tipo && (
                            <p className="text-xs text-gray-400 mt-0.5">{item.descripcion_tipo}</p>
                          )}
                        </td>
                        {/* Descripción */}
                        <td className="px-4 py-3 max-w-xs">
                          <p className="text-gray-700 truncate">{item.descripcion}</p>
                          {item.tipo === 'ingreso_nomina' && item.total_ingresos > 0 && (
                            <p className="text-xs text-green-700 font-semibold">RD$ {fmt(item.total_ingresos)}</p>
                          )}
                          {item.tipo === 'ausencia' && item.dias_solicitados > 0 && (
                            <p className="text-xs text-orange-700 font-semibold">{item.dias_solicitados} días</p>
                          )}
                        </td>
                        {/* Solicitado por */}
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-1 text-xs text-gray-500">
                            <User size={11} />
                            {item.solicitado_por || '—'}
                          </div>
                        </td>
                        {/* Fecha */}
                        <td className="px-4 py-3 whitespace-nowrap">
                          <div className="flex items-center gap-1 text-xs text-gray-500">
                            <Clock size={11} />
                            {fmtDate(item.fecha)}
                          </div>
                        </td>
                        {/* Acciones */}
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-1.5 justify-end">
                            <button
                              onClick={() => handleAprobarTabla(item)}
                              disabled={isProcesando}
                              className="flex items-center gap-1 px-2.5 py-1.5 text-xs font-semibold bg-green-600 hover:bg-green-700 text-white rounded-lg transition-colors disabled:opacity-50 whitespace-nowrap"
                            >
                              <CheckCircle size={12} />
                              Aprobar
                            </button>
                            <button
                              onClick={() => setRejectModal({ show: true, item, motivo: '' })}
                              disabled={isProcesando}
                              className="flex items-center gap-1 px-2.5 py-1.5 text-xs font-semibold bg-red-600 hover:bg-red-700 text-white rounded-lg transition-colors disabled:opacity-50 whitespace-nowrap"
                            >
                              <XCircle size={12} />
                              Rechazar
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}

      {/* ── VISTA HISTORIAL ───────────────────────────────────── */}
      {vista === 'historial' && (
        <div className="bg-white border border-gray-200 rounded-xl overflow-hidden">
          {cargandoHist ? (
            <div className="flex items-center justify-center py-16">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600" />
            </div>
          ) : historial.length === 0 ? (
            <div className="text-center py-16 text-gray-400 text-sm">Sin registros en el historial</div>
          ) : (
            <table className="w-full text-sm">
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr>
                  <th className="text-left px-4 py-3 font-semibold text-gray-600 text-xs">Empleado</th>
                  <th className="text-left px-4 py-3 font-semibold text-gray-600 text-xs">Tipo</th>
                  <th className="text-left px-4 py-3 font-semibold text-gray-600 text-xs">Descripción</th>
                  <th className="text-left px-4 py-3 font-semibold text-gray-600 text-xs">Procesado por</th>
                  <th className="text-left px-4 py-3 font-semibold text-gray-600 text-xs">Fecha</th>
                  <th className="text-left px-4 py-3 font-semibold text-gray-600 text-xs">Estado</th>
                  <th className="px-4 py-3" />
                </tr>
              </thead>
              <tbody>
                {historial.map(item => (
                  <tr
                    key={`${item.tipo}-${item.id}`}
                    className="border-t border-gray-100 hover:bg-blue-50 cursor-pointer"
                    onClick={() => handleVerDetalle(item)}
                  >
                    <td className="px-4 py-3">
                      <div className="font-medium text-gray-900">{item.empleado_nombre}</div>
                      {item.empleado_codigo && <div className="text-xs text-gray-400">#{item.empleado_codigo}</div>}
                    </td>
                    <td className="px-4 py-3">
                      <span className="text-xs font-medium text-gray-600 whitespace-nowrap">
                        {TIPO_LABELS[item.tipo] || item.tipo}
                      </span>
                      {item.descripcion_tipo && item.descripcion_tipo !== TIPO_LABELS[item.tipo] && (
                        <div className="text-xs text-gray-400">{item.descripcion_tipo}</div>
                      )}
                    </td>
                    <td className="px-4 py-3 max-w-xs">
                      <p className="text-gray-700 truncate">{item.descripcion}</p>
                      {item.tipo === 'ingreso_nomina' && item.total_ingresos > 0 && (
                        <p className="text-xs text-green-700 font-semibold">RD$ {fmt(item.total_ingresos)}</p>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-1 text-xs text-gray-500">
                        <User size={11} />
                        {item.procesado_por || '—'}
                      </div>
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap">
                      <div className="flex items-center gap-1 text-xs text-gray-500">
                        <Clock size={11} />
                        {fmtDate(item.fecha_proceso || item.fecha)}
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold capitalize ${ESTADO_BADGE[item.estado] || 'bg-gray-100 text-gray-600'}`}>
                        {(item.estado === 'aprobado'  || item.estado === 'aprobada')  && <CheckCircle size={10} />}
                        {(item.estado === 'rechazado' || item.estado === 'rechazada') && <XCircle size={10} />}
                        {item.estado}
                      </span>
                    </td>
                    <td className="px-4 py-3" onClick={e => e.stopPropagation()}>
                      <div className="flex items-center gap-1.5">
                        {onNavigateTo && (
                          <button
                            onClick={(e) => { e.stopPropagation(); handleVerDetalle(item); }}
                            className="flex items-center gap-1 px-2 py-1 text-xs font-medium text-blue-700 bg-blue-50 border border-blue-200 rounded-lg hover:bg-blue-100 transition-colors whitespace-nowrap"
                            title="Ver detalle completo"
                          >
                            <ExternalLink size={11} />
                            Ver
                          </button>
                        )}
                        {/* Revertir solo aplica a ingresos aprobados */}
                        {item.tipo === 'ingreso_nomina' && item.estado === 'aprobado' && (
                          <button
                            onClick={(e) => { e.stopPropagation(); setItemRevertir(item); setShowRevertModal(true); }}
                            className="flex items-center gap-1 px-2 py-1 text-xs font-medium text-amber-700 bg-amber-50 border border-amber-200 rounded-lg hover:bg-amber-100 transition-colors whitespace-nowrap"
                          >
                            <RotateCcw size={11} />
                            Revertir
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}

      {/* ── Modal Rechazar (vista tabla) ──────────────────────── */}
      {rejectModal.show && rejectModal.item && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md mx-4 p-6 space-y-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-red-100 rounded-xl flex items-center justify-center">
                <XCircle size={18} className="text-red-600" />
              </div>
              <div>
                <h3 className="font-semibold text-gray-900">Rechazar solicitud</h3>
                <p className="text-xs text-gray-500">{rejectModal.item.empleado_nombre} · {rejectModal.item.descripcion}</p>
              </div>
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Motivo del rechazo <span className="text-red-500">*</span></label>
              <textarea
                value={rejectModal.motivo}
                onChange={e => setRejectModal(prev => ({ ...prev, motivo: e.target.value }))}
                placeholder="Indica el motivo del rechazo"
                rows={3}
                className="w-full text-sm border border-gray-200 rounded-lg px-3 py-2 focus:outline-none focus:border-red-400 resize-none"
              />
            </div>
            <div className="flex gap-2 pt-1">
              <button
                onClick={handleRechazarTabla}
                disabled={!rejectModal.motivo.trim() || !!procesandoId}
                className="flex-1 px-4 py-2 bg-red-600 hover:bg-red-700 text-white text-sm font-semibold rounded-lg transition-colors disabled:opacity-50"
              >
                {procesandoId ? 'Procesando...' : 'Confirmar rechazo'}
              </button>
              <button
                onClick={() => setRejectModal({ show: false, item: null, motivo: '' })}
                className="px-4 py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 text-sm font-semibold rounded-lg transition-colors"
              >
                Cancelar
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Modal Revertir ────────────────────────────────────── */}
      {showRevertModal && itemRevertir && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md mx-4 p-6 space-y-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-amber-100 rounded-xl flex items-center justify-center">
                <RotateCcw size={18} className="text-amber-600" />
              </div>
              <div>
                <h3 className="font-semibold text-gray-900">Revertir aprobación</h3>
                <p className="text-xs text-gray-500">{itemRevertir.empleado_nombre} · {itemRevertir.descripcion}</p>
              </div>
            </div>
            <p className="text-sm text-gray-600">
              El ingreso volverá a estado <span className="font-semibold">pendiente</span> y podrá ser editado y re-enviado para aprobación.
            </p>
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Motivo de reversión <span className="text-red-500">*</span></label>
              <textarea
                value={motivoRevert}
                onChange={e => setMotivoRevert(e.target.value)}
                placeholder="Indica el motivo por el que se revierte esta aprobación"
                rows={3}
                className="w-full text-sm border border-gray-200 rounded-lg px-3 py-2 focus:outline-none focus:border-amber-400 resize-none"
              />
            </div>
            <div className="flex gap-2 pt-1">
              <button
                onClick={handleRevertir}
                disabled={!motivoRevert.trim() || !!revirtiendoId}
                className="flex-1 px-4 py-2 bg-amber-600 hover:bg-amber-700 text-white text-sm font-semibold rounded-lg transition-colors disabled:opacity-50"
              >
                {revirtiendoId ? 'Procesando...' : 'Confirmar reversión'}
              </button>
              <button
                onClick={() => { setShowRevertModal(false); setMotivoRevert(''); setItemRevertir(null); }}
                className="px-4 py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 text-sm font-semibold rounded-lg transition-colors"
              >
                Cancelar
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
